import { useEffect, useState } from "react";

import { CoreApiError } from "../../core-api/api-client";
import {
  loadGmailMailboxes,
  syncGmailIncremental,
  syncGmailRecovery,
} from "../../core-api/gmail";
import type { GmailMailbox } from "../../core-api/gmail";

export function InquiryCenterGmailSyncAction() {
  const [mailbox, setMailbox] = useState<GmailMailbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const mailboxId = mailbox?.id ?? "";
  const recoveryRequired = mailbox?.syncStatus === "RECOVERY_REQUIRED";

  useEffect(() => {
    let active = true;
    void loadGmailMailboxes()
      .then((mailboxes) => {
        if (!active) return;
        const selected = mailboxes.find(
          (item) =>
            item.status === "ACTIVE" &&
            item.authorizationStatus === "AUTHORIZED",
        );
        setMailbox(selected ?? null);
      })
      .catch(() => {
        if (active) setError("当前无法读取 Gmail 邮箱状态。");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const synchronize = async () => {
    if (!mailboxId || syncing || recovering) return;
    setSyncing(true);
    setNotice("");
    setError("");
    try {
      await syncGmailIncremental(mailboxId);
      setNotice(
        "同步任务已提交；首次授权会读取最近 24 小时的收件箱邮件。完成分析后，点击询单列表的“刷新”查看。",
      );
      const mailboxes = await loadGmailMailboxes();
      setMailbox(mailboxes.find((item) => item.id === mailboxId) ?? null);
    } catch (cause) {
      setError(
        cause instanceof CoreApiError && cause.status === 403
          ? "当前账号没有执行 Gmail 同步的权限。"
          : cause instanceof CoreApiError && cause.status === 409
            ? "邮箱检查点已失效，请点击“恢复 Gmail 同步（最近 30 天）”。"
            : "同步提交失败，请稍后重试或到“数据源状态”检查邮箱授权。",
      );
    } finally {
      setSyncing(false);
    }
  };

  const recover = async () => {
    if (!mailboxId || recovering || syncing) return;
    setRecovering(true);
    setNotice("");
    setError("");
    try {
      await syncGmailRecovery(mailboxId);
      setNotice(
        "恢复同步任务已提交（最近 30 天）；完成后新邮件会重新进入询单中心。",
      );
    } catch (cause) {
      setError(
        cause instanceof CoreApiError && cause.status === 403
          ? "当前账号没有执行 Gmail 恢复同步的权限。"
          : "恢复同步提交失败，请稍后重试或到“数据源状态”检查邮箱授权。",
      );
    } finally {
      setRecovering(false);
    }
  };

  return (
    <section className="inquiry-center-gmail-sync" aria-label="Gmail 邮件同步">
      {recoveryRequired ? (
        <button
          className="inquiry-center-gmail-sync-recovery"
          type="button"
          disabled={recovering}
          onClick={() => void recover()}
        >
          {recovering ? "正在恢复 Gmail…" : "恢复 Gmail 同步（最近 30 天）"}
        </button>
      ) : null}
      <button
        type="button"
        disabled={loading || syncing || recovering || !mailboxId}
        onClick={() => void synchronize()}
      >
        {syncing
          ? "正在同步 Gmail…"
          : loading
            ? "正在连接 Gmail…"
            : "同步 Gmail 最新邮件"}
      </button>
      {notice ? <span role="status">{notice}</span> : null}
      {error ? <span role="alert">{error}</span> : null}
      {recoveryRequired ? (
        <span role="status">
          邮箱检查点已失效，继续同步前需要先执行一次恢复同步。
        </span>
      ) : null}
      {!loading && !mailboxId && !error ? (
        <span role="status">Gmail 邮箱尚未完成只读授权。</span>
      ) : null}
    </section>
  );
}
